import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Smartphone, Bell, Check, Printer, CreditCard, MonitorSmartphone, Play, RotateCcw, ArrowRight } from "lucide-react";

const LOGO = "/pickfood-logo.webp";
const SCENES = [
  { icon: Smartphone, dur: 9, t: "1. Il cliente ordina dalla tua web app", d: "Apre il tuo link o QR, sceglie i prodotti e paga. Con il tuo nome, logo e colori — senza scaricare nulla." },
  { icon: Bell, dur: 9, t: "2. Ricevi subito la notifica", d: "Nuovo ordine con avviso sonoro persistente e web push, su smartphone, tablet o PC del locale." },
  { icon: Check, dur: 9, t: "3. Accetti e imposti il tempo", d: "Accetti o rifiuti, indichi il tempo di preparazione e aggiorni lo stato: il cliente lo vede in tempo reale." },
  { icon: Printer, dur: 9, t: "4. Stampi la comanda", d: "Comanda termica 80mm con prodotti, varianti, extra e note. Ristampa quando serve." },
  { icon: CreditCard, dur: 9, t: "5. Incasso istantaneo myPOS", d: "Il pagamento approvato arriva in pochi secondi sul tuo conto business myPOS. 0% commissioni Pickfood sugli ordini." },
  { icon: MonitorSmartphone, dur: 9, t: "6. Gestisci tutto da remoto", d: "Menu, prezzi, orari, disponibilità e statistiche da qualsiasi dispositivo. Ritiro e delivery." },
];
const TOTAL = SCENES.reduce((s, x) => s + x.dur, 0);

export default function VideoExplainer() {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [done, setDone] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!playing || done) return;
    ref.current = setTimeout(() => {
      if (i < SCENES.length - 1) setI(i + 1);
      else { setDone(true); setPlaying(false); }
    }, SCENES[i].dur * 1000);
    return () => clearTimeout(ref.current);
  }, [i, playing, done]);

  const restart = () => { setI(0); setDone(false); setPlaying(true); };
  const S = SCENES[i];

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-[#F5F3EF] flex flex-col">
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/"><span className="inline-flex bg-white rounded-lg px-2 py-1"><img src={LOGO} alt="Pickfood" className="h-6 w-auto object-contain" /></span></Link>
          <Link to="/demo"><Button className="brand-bg rounded-full font-semibold">Richiedi una demo</Button></Link>
        </div>
      </header>

      <div className="flex-1 grid place-items-center px-5 py-10">
        <div className="w-full max-w-3xl">
          <div className="text-center mb-6">
            <span className="text-xs font-bold uppercase tracking-[0.2em] brand-text">Come funziona · per l'esercente</span>
            <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">Dal tuo cliente al tuo incasso, in 1 minuto</h1>
          </div>

          {/* stage */}
          <div className="relative rounded-3xl border border-white/10 bg-white/[0.04] aspect-video overflow-hidden grid place-items-center">
            <div className="absolute -inset-10 rounded-full bg-[var(--brand)] opacity-10 blur-3xl animate-[glowfloat_6s_ease-in-out_infinite]" />
            <AnimatePresence mode="wait">
              <motion.div key={i} initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -24, scale: 0.96 }} transition={{ duration: 0.45 }}
                className="relative text-center px-8">
                <div className="w-20 h-20 rounded-2xl brand-bg grid place-items-center mx-auto mb-5 shadow-[0_0_50px_rgba(254,103,46,0.35)]">
                  <S.icon size={40} className="text-white" />
                </div>
                <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">{S.t}</h2>
                <p className="text-white/65 mt-3 max-w-lg mx-auto">{S.d}</p>
              </motion.div>
            </AnimatePresence>

            {done && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 bg-[#0B0B0D]/85 grid place-items-center">
                <div className="text-center">
                  <span className="inline-flex bg-white rounded-xl px-3 py-2 mb-4"><img src={LOGO} alt="Pickfood" className="h-8 w-auto object-contain" /></span>
                  <h2 className="font-head text-2xl font-extrabold">Il tuo locale. La tua web app. I tuoi incassi.</h2>
                  <div className="flex gap-3 justify-center mt-6">
                    <Link to="/demo"><Button className="brand-bg rounded-full h-12 px-7 font-semibold">Richiedi una demo <ArrowRight size={18} className="ml-1" /></Button></Link>
                    <Button variant="outline" onClick={restart} className="rounded-full h-12 px-6 border-white/20 text-white hover:bg-white/10 hover:text-white bg-transparent"><RotateCcw size={16} className="mr-2" />Rivedi</Button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* progress segments */}
          <div className="flex gap-1.5 mt-4">
            {SCENES.map((sc, idx) => (
              <div key={idx} className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <motion.div className="h-full brand-bg" initial={false}
                  animate={{ width: idx < i ? "100%" : idx === i && !done ? "100%" : idx === i && done ? "100%" : "0%" }}
                  transition={{ duration: idx === i && playing ? sc.dur : 0, ease: "linear" }} />
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-white/45">Scena {i + 1} di {SCENES.length} · durata totale ~{TOTAL}s</p>
            <div className="flex gap-2">
              {!done && <Button variant="ghost" onClick={() => setPlaying(!playing)} data-testid="video-toggle" className="text-white hover:bg-white/10 rounded-full">{playing ? "Pausa" : <><Play size={16} className="mr-1" />Riprendi</>}</Button>}
              {!done && i < SCENES.length - 1 && <Button variant="outline" onClick={() => setI(i + 1)} className="rounded-full border-white/20 text-white hover:bg-white/10 hover:text-white bg-transparent">Avanti</Button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
